import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarContext, useCalendarModel } from '../calendar/useCalendar.ts';
import { createSnapshotClient } from '../data/client.ts';
import { useCatalog } from '../data/useCatalog.ts';
import { useGameDetail } from '../data/useGameDetail.ts';
import { Freshness, SourceBanners } from '../features/catalog/Freshness.tsx';
import { GameList } from '../features/catalog/GameList.tsx';
import { PagedList } from '../features/catalog/PagedList.tsx';
import { EmptyState, ErrorBanner, ErrorState, OfflineState, Skeleton } from '../features/catalog/States.tsx';
import { type DrawerState, GameDrawer } from '../features/detail/GameDrawer.tsx';
import { ActiveFilters } from '../features/filters/ActiveFilters.tsx';
import { FilterBar, type PanelMode } from '../features/filters/FilterBar.tsx';
import { LayoutToggle } from '../features/filters/LayoutToggle.tsx';
import { QuickPlatforms } from '../features/filters/QuickPlatforms.tsx';
import { CalendarEntry } from '../features/gcal/CalendarEntry.tsx';
import { CalendarToast } from '../features/gcal/CalendarToast.tsx';
import { DriftBanner } from '../features/gcal/DriftBanner.tsx';
import { HeroCarousel } from '../features/home/HeroCarousel.tsx';
import { SearchTrigger } from '../features/search/SearchTrigger.tsx';
import { Logo, Rail, TabBar } from '../features/shell/Nav.tsx';
import { activeSection } from '../features/shell/nav.ts';
import { InstallButton, OfflineBanner, UpdateToast } from '../features/shell/Pwa.tsx';
import { useHideOnScroll } from '../features/shell/useHideOnScroll.ts';
import { markTourSeen, shouldShowTour } from '../features/tour/tour.ts';
import { monthOfDay } from '../filters/calendar.ts';
import { applyFilters, isGroupedSort, sortItems, toEntries } from '../filters/catalog.ts';
import { activeFilters, clearFilter, type FilterState, serializeState } from '../filters/state.ts';
import { heroPicks, localEpochDay } from '../filters/timeline.ts';
import { useFilterState } from '../filters/useFilterState.ts';
import { t } from '../i18n/zh-TW.ts';
import { cardCover, withViewTransition } from '../lib/motion.ts';
import { usePwa } from '../pwa/usePwa.ts';
import { ArrowUp, Compass, ICON, SlidersHorizontal } from '../ui/icons/index.ts';
import { ThemeSwitcher } from '../ui/ThemeSwitcher.tsx';
import { useTheme } from '../ui/theme/useTheme.ts';
import { BREAKPOINTS, useMediaQuery } from '../ui/useMediaQuery.ts';
import { ErrorBoundary } from './ErrorBoundary.tsx';

const client = createSnapshotClient();

// 按需載入（設計稿 §9）：月曆、指令面板、新手導覽不在首頁需要的程式裡，用到時才下載
const CalendarView = lazy(() =>
  import('../features/calendar/CalendarView.tsx').then((m) => ({ default: m.CalendarView })),
);
const CommandPalette = lazy(() =>
  import('../features/search/CommandPalette.tsx').then((m) => ({ default: m.CommandPalette })),
);
const Tour = lazy(() => import('../features/tour/Tour.tsx').then((m) => ({ default: m.Tour })));
const CalendarPanel = lazy(() =>
  import('../features/gcal/CalendarPanel.tsx').then((m) => ({ default: m.CalendarPanel })),
);

/** 頂部列的實際高度：寫進 CSS 變數讓分組標題 sticky 在它下方，也提供給虛擬清單計算可見範圍 */
function useBarHeight() {
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const h = Math.round(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--bar-h', `${h}px`);
      setHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, height] as const;
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setShow(window.scrollY > 1200));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  if (!show) return null;
  return (
    <button type="button" className="back-to-top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
      <ArrowUp {...ICON} />
      {t.list.backToTop}
    </button>
  );
}

export function App() {
  const [state, update] = useFilterState();
  // 月曆：不分未發售／已發售，載入該月份需要的分片（過去的月份會載入當年的已發售分片）
  const calendarMonth = state.month;
  const catalogState = useMemo(
    (): FilterState =>
      calendarMonth ? { ...state, status: ['up', 'rel'], from: calendarMonth, to: calendarMonth } : state,
    [state, calendarMonth],
  );
  const catalog = useCatalog(catalogState, client);
  const [barRef, barHeight] = useBarHeight();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const phone = useMediaQuery(BREAKPOINTS.phone);
  const tablet = useMediaQuery(BREAKPOINTS.tablet);
  const filterToggleRef = useRef<HTMLButtonElement>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const pwa = usePwa();
  // 離線時載入失敗：恢復連線後自動重試（離線畫面的承諾）
  const failedOffline = catalog.status === 'error' && pwa.offline;
  const wasFailedOffline = useRef(false);
  const retryCatalog = catalog.retry;
  useEffect(() => {
    if (wasFailedOffline.current && !pwa.offline) retryCatalog();
    wasFailedOffline.current = failedOffline;
  }, [failedOffline, pwa.offline, retryCatalog]);
  // App 捷徑（manifest shortcuts）：?m=now 打開本月月曆、?search=1 打開搜尋。
  // 要在網址被正規化之前讀取（不認得的參數會被移除）
  const [launch] = useState(() => new URLSearchParams(window.location.search));
  // Google 行事曆（GDO-24～GDO-27）：「我的平台」＝篩選中選的平台
  const calendar = useCalendarModel({ platforms: state.platforms, launch });
  const allRows = useMemo(() => catalog.items.map((i) => i.row), [catalog.items]);
  const panelMode: PanelMode = phone ? 'sheet' : tablet ? 'drawer' : 'popover';
  useHideOnScroll(phone);
  const activeCount = activeFilters(state).length;
  const now = useMemo(() => Date.now(), []);
  const currentYear = new Date(now + 8 * 3600_000).getUTCFullYear();
  /** 使用者時區的今天：倒數、本週／下週 */
  const localToday = useMemo(() => localEpochDay(now), [now]);

  const { status, platforms, genres, q, from, to, sort } = state;
  // 只在影響結果的條件改變時重算（換頁、切換顯示方式不需要）
  const criteria = useMemo(
    () => ({ status, platforms, genres, q, from, to }),
    [status, platforms, genres, q, from, to],
  );
  const filtered = useMemo(
    () => sortItems(applyFilters(catalog.items, criteria), sort),
    [catalog.items, criteria, sort],
  );
  const total = useMemo(
    () => applyFilters(catalog.items, { status, platforms: [], q: '', from: null, to: null }).length,
    [catalog.items, status],
  );
  // 與「即將發售」清單相同的判定（含今天發售、已在其他平台發售但新平台版還沒推出的遊戲）
  const upcomingCount = useMemo(
    () => applyFilters(catalog.items, { status: ['up'], platforms: [], q: '', from: null, to: null }).length,
    [catalog.items],
  );
  const grouped = isGroupedSort(sort);
  // 近到遠排序時分出本週／下週
  const timelineToday = sort === 'date' ? localToday : null;
  const entries = useMemo(
    () => (state.view === 'scroll' ? toEntries(filtered, grouped, timelineToday) : []),
    [filtered, grouped, timelineToday, state.view],
  );
  const calendarItems = useMemo(
    () =>
      calendarMonth
        ? applyFilters(catalog.items, {
            status: ['up', 'rel'],
            platforms,
            genres,
            q,
            from: calendarMonth,
            to: calendarMonth,
          })
        : [],
    [catalog.items, calendarMonth, platforms, genres, q],
  );
  // 首頁輪播：只在「即將發售」、沒有搜尋與日期範圍時顯示（套用平台篩選）
  const showHero = activeSection(state) === 'upcoming' && !q && !from && !to;
  const hero = useMemo(() => (showHero ? heroPicks(filtered, localToday) : null), [showHero, filtered, localToday]);

  // 開關詳情：寫進網址（可分享、上一頁可回到開啟前）。
  // 記下點開的卡片封面，詳情開啟時封面從這裡飛過去；開關的動畫由 GameDrawer 負責（GDO-35）
  const [origin, setOrigin] = useState<{ id: string; el: HTMLElement | null } | null>(null);
  const openGame = useCallback(
    (id: string) => {
      setOrigin({ id, el: cardCover(id) });
      update({ game: id });
    },
    [update],
  );
  const closeGame = useCallback(() => update({ game: null }), [update]);
  // 一次性的切換（常用平台、移除條件膠囊、版面、導覽）：保留下來的卡片移到新位置，其他淡入淡出。
  // 篩選面板裡的操作不做轉場：面板是連續多選的地方，每次點擊都要立刻反應
  const updateAnimated = useCallback(
    (u: Parameters<typeof update>[0], mode?: 'push' | 'replace') =>
      withViewTransition(() => update(u, mode), 'reorder'),
    [update],
  );
  const detail = useGameDetail(catalog.game?.row ?? null, client);
  const drawer: DrawerState | null = !catalog.game
    ? null
    : catalog.game.status === 'missing'
      ? { kind: 'missing' }
      : detail.status === 'ready' && detail.detail
        ? { kind: 'ready', detail: detail.detail }
        : detail.status === 'error'
          ? { kind: 'error', error: detail.error, retry: detail.retry }
          : catalog.game.status === 'loading' && catalog.error
            ? { kind: 'error', error: catalog.error, retry: catalog.retry }
            : { kind: 'loading' };

  const onPage = (page: number) => {
    update({ page });
    window.scrollTo({ top: 0 });
  };

  // 手機分頁列的「搜尋」：回到頂端、顯示頂部列並開啟指令面板
  const focusSearch = useCallback(() => {
    window.scrollTo({ top: 0 });
    delete document.documentElement.dataset.barHidden;
    setPaletteOpen(true);
  }, []);

  // 指令面板快捷鍵：⌘K／Ctrl+K 任何地方都可以；「/」在沒有輸入文字時
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      const el = e.target as HTMLElement | null;
      const typing = el?.closest('input, textarea, select, [contenteditable="true"]');
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  // 新手導覽：第一次造訪、資料載入完成後自動開始（直接打開詳情或月曆的連結不打斷）；之後可以從「使用導覽」重看
  const [tourOpen, setTourOpen] = useState(false);
  const tourChecked = useRef(false);
  useEffect(() => {
    if (tourChecked.current || catalog.status !== 'ready') return;
    tourChecked.current = true;
    if (!state.game && !state.month && shouldShowTour()) setTourOpen(true);
  }, [catalog.status, state.game, state.month]);
  const startTour = useCallback(() => {
    setFiltersOpen(false);
    window.scrollTo({ top: 0 });
    setTourOpen(true);
  }, []);
  const tourButton = (
    <button type="button" className="link-button tour-link" onClick={startTour}>
      <Compass {...ICON} />
      {t.tour.replay}
    </button>
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: 只在開啟時處理一次捷徑
  useEffect(() => {
    if (launch.get('m') === 'now') update({ month: monthOfDay(localToday) }, 'replace');
    if (launch.has('search')) setPaletteOpen(true);
  }, []);

  // 資料載入前先保留同樣高度的位置，載入後不推動側欄的其他元素（避免版面位移）
  const freshness = catalog.manifest ? (
    <Freshness manifest={catalog.manifest} now={now} />
  ) : (
    <span className="freshness freshness-pending" aria-hidden="true" />
  );
  const themeSwitcher = <ThemeSwitcher pref={theme} onChange={setTheme} />;

  return (
    <CalendarContext.Provider value={calendar}>
      <div className="shell">
        <a className="skip-link" href="#list">
          {t.app.skipToList}
        </a>
        {!phone && (
          <Rail
            state={state}
            update={updateAnimated}
            upcomingCount={catalog.status === 'ready' ? upcomingCount : undefined}
            footer={
              <>
                <InstallButton pwa={pwa} />
                <CalendarEntry rows={allRows} />
                {tourButton}
                {freshness}
                {themeSwitcher}
              </>
            }
          />
        )}
        <div className="main-col">
          <header ref={barRef} className="app-bar">
            <div className="app-bar-row">
              {phone && <Logo as="h1" />}
              <SearchTrigger query={state.q} onOpen={() => setPaletteOpen(true)} />
              {!phone && <QuickPlatforms state={state} update={updateAnimated} />}
              <LayoutToggle value={state.layout} onChange={(layout) => updateAnimated({ layout })} />
              <button
                ref={filterToggleRef}
                type="button"
                className="filters-toggle"
                aria-expanded={filtersOpen}
                aria-controls="filter-bar"
                onClick={() => setFiltersOpen((o) => !o)}
              >
                <SlidersHorizontal {...ICON} />
                {t.filters.title}
                {activeCount > 0 && <span className="count">{activeCount}</span>}
              </button>
            </div>
            <ActiveFilters state={state} update={updateAnimated} withPlatforms={phone} />
            <FilterBar
              state={state}
              update={update}
              open={filtersOpen}
              mode={panelMode}
              onClose={closeFilters}
              toggleRef={filterToggleRef}
              footer={
                phone && (
                  <>
                    <InstallButton pwa={pwa} />
                    <CalendarEntry rows={allRows} />
                    {tourButton}
                    {freshness}
                    {themeSwitcher}
                  </>
                )
              }
            />
          </header>

          <main id="list" className="content">
            {pwa.offline && <OfflineBanner manifest={catalog.manifest} />}
            {catalog.manifest && <SourceBanners manifest={catalog.manifest} />}
            {catalog.status === 'ready' && catalog.error && (
              <ErrorBanner error={catalog.error} onRetry={catalog.retry} />
            )}
            {catalog.status === 'ready' && <DriftBanner rows={allRows} />}
            <ErrorBoundary name="list">
              {catalog.status === 'loading' && <Skeleton layout={state.layout} />}
              {catalog.status === 'error' &&
                (pwa.offline ? (
                  <OfflineState onRetry={catalog.retry} />
                ) : (
                  <ErrorState error={catalog.error} onRetry={catalog.retry} />
                ))}
              {catalog.status === 'ready' && calendarMonth && (
                <Suspense fallback={<Skeleton layout="grid" count={3} />}>
                  <CalendarView
                    items={calendarItems}
                    month={calendarMonth}
                    today={localToday}
                    phone={phone}
                    hrefFor={(month) => `${window.location.pathname}${serializeState({ ...state, month, game: null })}`}
                    onMonth={(month) => update({ month, game: null })}
                    onOpen={openGame}
                  />
                </Suspense>
              )}
              {catalog.status === 'ready' && !calendarMonth && (
                <>
                  {hero && (
                    <ErrorBoundary name="hero">
                      <HeroCarousel
                        mode={hero.mode}
                        items={hero.items}
                        today={localToday}
                        currentYear={currentYear}
                        onOpen={openGame}
                      />
                    </ErrorBoundary>
                  )}
                  <p className="summary" aria-live="polite">
                    {t.list.summary(filtered.length, total)}
                    {catalog.loadingMore && <span className="loading-more"> · {t.list.loading}</span>}
                  </p>
                  {filtered.length === 0 ? (
                    <EmptyState active={activeFilters(state)} onClear={(f) => update(clearFilter(state, f))} />
                  ) : state.view === 'scroll' ? (
                    <GameList
                      entries={entries}
                      layout={state.layout}
                      currentYear={currentYear}
                      today={localToday}
                      stickyOffset={barHeight}
                      onOpen={openGame}
                    />
                  ) : (
                    <PagedList
                      items={filtered}
                      layout={state.layout}
                      grouped={grouped}
                      page={state.page}
                      pageSize={state.pageSize}
                      currentYear={currentYear}
                      today={timelineToday}
                      countdownToday={localToday}
                      onPage={onPage}
                      onOpen={openGame}
                    />
                  )}
                </>
              )}
            </ErrorBoundary>
          </main>
          <footer className="footer">
            {t.app.attribution}
            <span className="footer-links">
              <a href="/privacy/">{t.app.privacy}</a>
              <a href="/terms/">{t.app.terms}</a>
            </span>
          </footer>
        </div>
        {phone && <TabBar state={state} update={updateAnimated} onSearch={focusSearch} />}
        {pwa.updateReady && <UpdateToast onApply={pwa.applyUpdate} />}
        {tourOpen && (
          <ErrorBoundary name="tour">
            <Suspense fallback={null}>
              <Tour
                phone={phone}
                onClose={(how) => {
                  markTourSeen(how);
                  setTourOpen(false);
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {paletteOpen && (
          <ErrorBoundary name="palette">
            <Suspense fallback={null}>
              <CommandPalette
                items={catalog.items}
                initialQuery={state.q}
                currentYear={currentYear}
                onClose={() => setPaletteOpen(false)}
                onOpenGame={(id) => {
                  setPaletteOpen(false);
                  openGame(id);
                }}
                onSearchList={(value) => {
                  setPaletteOpen(false);
                  update({ q: value, page: 1, month: null });
                }}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        <BackToTop />
        {drawer && (
          <ErrorBoundary name="detail">
            <GameDrawer
              state={drawer}
              name={catalog.game?.row?.n}
              row={catalog.game?.row ?? undefined}
              currentYear={currentYear}
              now={now}
              today={localToday}
              phone={phone}
              origin={origin?.id === state.game ? origin.el : null}
              onClose={closeGame}
            />
          </ErrorBoundary>
        )}
        {calendar.panelOpen && (
          <ErrorBoundary name="calendar">
            <Suspense fallback={null}>
              <CalendarPanel items={catalog.items} currentYear={currentYear} onOpenGame={openGame} />
            </Suspense>
          </ErrorBoundary>
        )}
        <CalendarToast />
      </div>
    </CalendarContext.Provider>
  );
}
